import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  PropsWithChildren,
  useContext,
} from 'react';
import { supabase } from '../supabaseClient';
import { CacheContext } from '../context/CacheContext';
import { AuthContext } from '../context/AuthContext';
import {
  UserRecord,
  PostData,
  FeedData,
  FeedTagData,
  FollowData,
  VoteData,
} from '../model/types';
import { RealtimeChannel } from '@supabase/supabase-js';

const POSTS_PAGE_SIZE = 100;
const POLL_INTERVAL_MS = 15000;
const MAX_POSTS = 2000; // cap total loaded posts

export const CacheProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useContext(AuthContext);

  const [posts, setPosts] = useState<PostData[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const postsOffsetRef = useRef(0);

  const feedCacheRef = useRef<Map<string, FeedData>>(new Map());
  const [feeds, setFeeds] = useState<FeedData[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(true);

  const userCacheRef = useRef<Map<string, UserRecord>>(new Map());
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [follows, setFollows] = useState<FollowData[]>([]);
  const [followsLoading, setFollowsLoading] = useState(true);
  const [votes, setVotes] = useState<VoteData[]>([]);
  const [votesLoading, setVotesLoading] = useState(true);
  const [feedTags, setFeedTags] = useState<FeedTagData[]>([]);

  const channelRef = useRef<RealtimeChannel | null>(null);

  // ─── Fetch posts ──────────────────────────────────────────────────────────
  // Used for BOTH initial load AND the polling fallback. The polling
  // path must NOT clobber pages that infinite scroll has already
  // paginated in — earlier versions replaced state with just the newest
  // 50, which destroyed the user's loaded-more window every 5s. Now we
  // merge the newest page into whatever is already there.
  const refreshPosts = useCallback((opts: { initial?: boolean } = {}) => {
    supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(POSTS_PAGE_SIZE)
      .then(({ data, error }) => {
        if (!error && data) {
          const fresh = data as PostData[];
          if (opts.initial) {
            setPosts(fresh);
            postsOffsetRef.current = fresh.length;
            setHasMorePosts(fresh.length === POSTS_PAGE_SIZE);
          } else {
            // Merge: upsert fresh rows by id, keep everything else,
            // re-sort by created_at desc. Preserves paginated pages.
            setPosts((prev) => {
              const byId = new Map<string, PostData>(prev.map((p) => [p.id, p]));
              fresh.forEach((p) => byId.set(p.id, p));
              const merged = Array.from(byId.values()).sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
              );
              // Only grow postsOffsetRef — never shrink. Ensures
              // loadMorePosts continues to page forward from the
              // deepest point we've loaded so far.
              if (merged.length > postsOffsetRef.current) {
                postsOffsetRef.current = merged.length;
              }
              return merged;
            });
          }
        }
        setPostsLoading(false);
      });
  }, []);

  // ─── Load more posts (pagination) ────────────────────────────────────────
  const loadMorePosts = useCallback(() => {
    if (loadingMorePosts || !hasMorePosts) return;
    if (postsOffsetRef.current >= MAX_POSTS) { setHasMorePosts(false); return; }
    setLoadingMorePosts(true);
    supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false })
      .range(postsOffsetRef.current, postsOffsetRef.current + POSTS_PAGE_SIZE - 1)
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          setPosts((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            const fresh = (data as PostData[]).filter((p) => !ids.has(p.id));
            const merged = [...prev, ...fresh];
            postsOffsetRef.current = merged.length;
            return merged;
          });
          if (data.length < POSTS_PAGE_SIZE) setHasMorePosts(false);
        } else {
          setHasMorePosts(false);
        }
        setLoadingMorePosts(false);
      });
  }, [loadingMorePosts, hasMorePosts]);

  // ─── Initial load — public data, no auth required ────────────────────────
  useEffect(() => {
    setPostsLoading(true);
    refreshPosts({ initial: true });

    setFeedsLoading(true);
    supabase
      .from('feeds')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          const feedList = data as FeedData[];
          setFeeds(feedList);
          feedList.forEach((f) => {
            feedCacheRef.current.set(f.id, f);
            feedCacheRef.current.set(f.feed_tag, f);
          });
        }
        setFeedsLoading(false);
      });

    setUsersLoading(true);
    supabase
      .from('users')
      .select('*')
      .then(({ data, error }) => {
        if (!error && data) {
          const userList = data as UserRecord[];
          setUsers(userList);
          userList.forEach((u) => userCacheRef.current.set(u.id, u));
        }
        setUsersLoading(false);
      });

    setFollowsLoading(true);
    supabase
      .from('follows')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setFollows(data as FollowData[]);
        setFollowsLoading(false);
      });

    supabase
      .from('feed_tags')
      .select('*')
      .then(({ data, error }) => {
        if (!error && data) setFeedTags(data as FeedTagData[]);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Load votes only when authenticated ───────────────────────────────────
  useEffect(() => {
    if (!user) { setVotes([]); setVotesLoading(false); return; }
    setVotesLoading(true);
    supabase
      .from('votes')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) setVotes(data as VoteData[]);
        setVotesLoading(false);
      });
  }, [user]);

  // ─── Polling fallback: refresh posts every 5s ────────────────────────────
  useEffect(() => {
    const interval = setInterval(refreshPosts, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshPosts]);

  // ─── Supabase Realtime — optimistic prepend on INSERT, merge on UPDATE ───
  useEffect(() => {
    const channel = supabase
      .channel('cache-realtime')
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'posts' },
        (payload: any) => {
          setPosts((prev) => {
            if (prev.some((p) => p.id === payload.new.id)) return prev;
            return [payload.new as PostData, ...prev];
          });
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'posts' },
        (payload: any) => {
          const updated = payload.new as PostData;
          setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'DELETE', schema: 'public', table: 'posts' },
        (payload: any) => {
          setPosts((prev) => prev.filter((p) => p.id !== payload.old.id));
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'follows' },
        (payload: any) => {
          setFollows((prev) => {
            if (prev.some((f) => f.id === payload.new.id)) return prev;
            return [payload.new as FollowData, ...prev];
          });
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'follows' },
        (payload: any) => {
          const updated = payload.new as FollowData;
          setFollows((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'DELETE', schema: 'public', table: 'follows' },
        (payload: any) => {
          setFollows((prev) => prev.filter((f) => f.id !== payload.old.id));
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'users' },
        (payload: any) => {
          const updated = payload.new as UserRecord;
          userCacheRef.current.set(updated.id, updated);
          setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        },
      )
      // Feeds — so new #hashtag feeds appear in trending/feeds list
      // without a page refresh.
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'feeds' },
        (payload: any) => {
          const inserted = payload.new as FeedData;
          feedCacheRef.current.set(inserted.id, inserted);
          feedCacheRef.current.set(inserted.feed_tag, inserted);
          setFeeds((prev) => {
            if (prev.some((f) => f.id === inserted.id)) return prev;
            return [inserted, ...prev];
          });
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'feeds' },
        (payload: any) => {
          const updated = payload.new as FeedData;
          feedCacheRef.current.set(updated.id, updated);
          feedCacheRef.current.set(updated.feed_tag, updated);
          setFeeds((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'DELETE', schema: 'public', table: 'feeds' },
        (payload: any) => {
          const removedId = payload.old.id;
          setFeeds((prev) => {
            const gone = prev.find((f) => f.id === removedId);
            if (gone) {
              feedCacheRef.current.delete(gone.id);
              feedCacheRef.current.delete(gone.feed_tag);
            }
            return prev.filter((f) => f.id !== removedId);
          });
        },
      )
      // feed_tags — so "activity per feed" counts update live
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'feed_tags' },
        (payload: any) => {
          setFeedTags((prev) => {
            if (prev.some((ft) => ft.id === payload.new.id)) return prev;
            return [...prev, payload.new as FeedTagData];
          });
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'DELETE', schema: 'public', table: 'feed_tags' },
        (payload: any) => {
          setFeedTags((prev) => prev.filter((ft) => ft.id !== payload.old.id));
        },
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Accessor functions ───────────────────────────────────────────────────

  const getUserRecord = useCallback(
    (id: string): UserRecord | undefined =>
      userCacheRef.current.get(id) ?? users.find((u) => u.id === id),
    [users],
  );

  const getUserRecordFromUsername = useCallback(
    (username: string): UserRecord | undefined =>
      users.find((u) => u.username === username),
    [users],
  );

  const getPost = useCallback(
    (id: string): PostData | undefined => posts.find((p) => p.id === id),
    [posts],
  );

  const getFollow = useCallback(
    (id: string): FollowData | undefined => follows.find((f) => f.id === id),
    [follows],
  );

  const getFeedFromTag = useCallback(
    (tag: string): FeedData | undefined =>
      feedCacheRef.current.get(tag) ?? feeds.find((f) => f.feed_tag === tag),
    [feeds],
  );

  const getFeedFromID = useCallback(
    (id: string): FeedData | undefined =>
      feedCacheRef.current.get(id) ?? feeds.find((f) => f.id === id),
    [feeds],
  );

  const allUsers = useCallback(() => users, [users]);
  const allFeeds = useCallback(() => feeds, [feeds]);
  const allPosts = useCallback(() => {
    // Enrich posts with computed reply_count and vote_count from in-memory data
    const replyMap: Record<string, number> = {};
    const voteMap: Record<string, number> = {};
    posts.forEach((p) => { if (p.parent_id) replyMap[p.parent_id] = (replyMap[p.parent_id] || 0) + 1; });
    votes.forEach((v) => { if (v.object_type === 'posts') voteMap[v.parent_id] = (voteMap[v.parent_id] || 0) + 1; });
    return posts.map((p) => ({
      ...p,
      reply_count: p.reply_count ?? replyMap[p.id] ?? 0,
      vote_count: p.vote_count ?? voteMap[p.id] ?? 0,
    }));
  }, [posts, votes]);
  const allFollows = useCallback(() => follows, [follows]);
  const allVotes = useCallback(() => votes, [votes]);
  const allFeedTags = useCallback(() => feedTags, [feedTags]);

  const loadingStatus = {
    users: usersLoading,
    posts: postsLoading,
    feeds: feedsLoading,
    follows: followsLoading,
    votes: votesLoading,
  };

  return (
    <CacheContext.Provider
      value={{
        getUserRecord,
        getUserRecordFromUsername,
        getPost,
        allUsers,
        allFeeds,
        allPosts,
        allFollows,
        allVotes,
        allFeedTags,
        getFeedFromTag,
        getFeedFromID,
        getFollow,
        loadMorePosts,
        hasMorePosts,
        loadingMorePosts,
        loadingStatus,
      }}
    >
      {children}
    </CacheContext.Provider>
  );
};
